import type { Task, TaskStatus } from '@friday/shared/realtime'

export type BulkEditField = 'dueDate' | 'assigneeUserId' | 'storyPoints' | 'milestoneId' | 'status'
export type BulkEditValue = string | number | null
export type BulkTaskPatch = Pick<Task, BulkEditField>

export function patchForBulkField(field: BulkEditField, value: BulkEditValue): Record<BulkEditField, never> | Record<string, unknown> {
  switch (field) {
    case 'dueDate': {
      if (value === null) return { dueDate: null }
      if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error('Choose a valid due date')
      const date = new Date(`${value}T00:00:00.000Z`)
      if (Number.isNaN(date.getTime())) throw new Error('Choose a valid due date')
      return { dueDate: date.toISOString() }
    }
    case 'assigneeUserId':
    case 'milestoneId':
      if (value !== null && (typeof value !== 'string' || value.length === 0)) throw new Error('Choose a valid value')
      return { [field]: value }
    case 'storyPoints':
      if (value === null) return { storyPoints: null }
      if (typeof value !== 'number' || !Number.isInteger(value) || value < 0 || value > 99) throw new Error('Story points must be an integer from 0 to 99')
      return { storyPoints: value }
    case 'status': {
      const statuses: TaskStatus[] = ['open', 'in_progress', 'in_review', 'done', 'blocked', 'cancelled']
      if (typeof value !== 'string' || !statuses.includes(value as TaskStatus)) throw new Error('Choose a valid status')
      return { status: value }
    }
  }
}

/** Keeps selection scoped to the current list view and its visible rows. */
export function selectionForVisible(selected: ReadonlySet<string>, visibleIds: ReadonlySet<string>): Set<string> {
  return new Set([...selected].filter((id) => visibleIds.has(id)))
}

export function toggleVisibleSelection(selected: ReadonlySet<string>, visibleIds: ReadonlySet<string>, select: boolean): Set<string> {
  const next = new Set(selected)
  for (const id of visibleIds) {
    if (select) next.add(id)
    else next.delete(id)
  }
  return next
}

export function retainFailedSelection(selected: ReadonlySet<string>, failedIds: ReadonlySet<string>): Set<string> {
  return new Set([...selected].filter((id) => failedIds.has(id)))
}
